import { apiFetch, apiFetchBlob } from '@/lib/supabaseClient';

export const PERSONNEL_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
];
export const PERSONNEL_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export function safePersonnelDocumentFilename(value, mimeType) {
  const extension = mimeType === 'application/pdf' ? '.pdf' : mimeType === 'image/png' ? '.png' : '.jpg';
  const clean = String(value || `documento${extension}`)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\\/:*?"<>|\r\n]/g, '_')
    .replace(/[^a-zA-Z0-9_.-]/g, '_')
    .slice(0, 140);
  const stem = clean.replace(/\.(pdf|jpe?g|png)$/i, '') || 'documento';
  return `${stem}${extension}`;
}

function nativeDocuments() {
  const cap = window.Capacitor;
  if (cap?.isNativePlatform?.() !== true) return null;
  if (cap.getPlatform?.() !== 'ios') throw new Error('Condivisione documento non disponibile su questo dispositivo.');
  const plugin = cap.Plugins?.RestaurantOSDocuments
    || (typeof cap.registerPlugin === 'function' ? cap.registerPlugin('RestaurantOSDocuments') : null);
  if (!plugin) throw new Error('Aggiorna l’app per condividere il documento.');
  return plugin;
}

function asBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(new Error('Documento non disponibile.'));
    reader.readAsDataURL(blob);
  });
}

function validateBlob(blob, expectedMime) {
  const blobMime = String(blob?.type || '').split(';')[0].toLowerCase();
  const actualMime = blobMime || String(expectedMime || '').split(';')[0].toLowerCase();
  if (!blob?.size || blob.size > PERSONNEL_DOCUMENT_MAX_BYTES || !PERSONNEL_DOCUMENT_MIME_TYPES.includes(actualMime)) {
    throw new Error('Il documento non è disponibile o supera il limite consentito.');
  }
  if (blobMime && !PERSONNEL_DOCUMENT_MIME_TYPES.includes(blobMime)) {
    throw new Error('Il formato del documento non è supportato.');
  }
  return actualMime;
}

function authenticatedBlobPath(value) {
  const link = String(value || '');
  if (link.startsWith('/') && !link.startsWith('//')) return link;
  const backendBase = process.env.REACT_APP_BACKEND_URL;
  if (!backendBase) throw new Error('Link temporaneo non disponibile.');
  let backend;
  let parsed;
  try {
    backend = new URL(backendBase);
    parsed = new URL(link, backend);
  } catch {
    throw new Error('Link temporaneo non disponibile.');
  }
  if (parsed.origin !== backend.origin) throw new Error('Link temporaneo non disponibile.');
  const basePath = backend.pathname.replace(/\/+$/, '');
  const path = basePath && parsed.pathname.startsWith(`${basePath}/`)
    ? parsed.pathname.slice(basePath.length)
    : parsed.pathname;
  return `${path}${parsed.search}`;
}

/**
 * Resolves a short-lived authenticated access-link, then streams the file via
 * the authenticated API client. No public or persistent storage URL is kept.
 */
export async function downloadPersonnelDocument({ accessLinkEndpoint, filename, mimeType, disposition = 'attachment' }) {
  const inline = disposition === 'inline';
  const nativePlatform = window.Capacitor?.isNativePlatform?.() === true;
  const iosBrowser = !nativePlatform && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const reserved = (inline || iosBrowser) && !nativePlatform ? window.open('about:blank', '_blank') : null;
  if (reserved) reserved.opener = null;
  let objectUrl;
  try {
    const access = await apiFetch(accessLinkEndpoint);
    if (!access?.url || !Number.isFinite(Number(access.expires_in)) || Number(access.expires_in) <= 0 || Number(access.expires_in) > 60) {
      throw new Error('Link temporaneo non disponibile.');
    }
    const blob = await apiFetchBlob(authenticatedBlobPath(access.url));
    const actualMimeType = validateBlob(blob, mimeType);
    const safeName = safePersonnelDocumentFilename(filename, actualMimeType);
    const native = nativeDocuments();
    if (native) {
      const base64 = await asBase64(blob);
      const share = actualMimeType === 'application/pdf' ? native.sharePdf : native.shareDocument;
      if (typeof share !== 'function') throw new Error('Aggiorna l’app per condividere questo documento.');
      const result = actualMimeType === 'application/pdf'
        ? await share.call(native, { filename: safeName, base64 })
        : await share.call(native, { filename: safeName, base64, mimeType: actualMimeType });
      if (typeof result?.cancelled !== 'boolean') throw new Error('Condivisione documento non confermata.');
      return { cancelled: result.cancelled };
    }

    objectUrl = URL.createObjectURL(blob);
    if (inline || iosBrowser) {
      if (reserved && !reserved.closed) {
        reserved.location.href = objectUrl;
      } else {
        const preview = document.createElement('a');
        preview.href = objectUrl;
        preview.target = '_blank';
        preview.rel = 'noopener';
        document.body.appendChild(preview);
        preview.click();
        preview.remove();
      }
    } else {
      const download = document.createElement('a');
      download.href = objectUrl;
      download.download = safeName;
      document.body.appendChild(download);
      download.click();
      download.remove();
    }
    return { cancelled: false };
  } catch (error) {
    if (reserved && !reserved.closed) reserved.close();
    throw error;
  } finally {
    if (objectUrl) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }
}
